<template>
  <v-navigation-drawer
    app
    permanent
    class="app-drawer"
    v-model="drawer"
    :width="drawerWidth"
    :mini-variant="mini"
    dark
  >
    <!-- Header: logo + hamburger collapse + map link -->
    <div class="drawer-header">
      <div class="drawer-logo" v-if="!mini">
        <img :src="logoUrl" height="36" alt="Kalan" />
      </div>
      <div class="drawer-header-actions" :class="{ 'is-mini': mini }">
        <button
          type="button"
          class="drawer-icon-btn"
          data-tip="Compactar menu"
          @click="handleDrawerCollapse"
        >
          <v-icon size="20" color="rgba(255,255,255,0.7)">mdi-menu</v-icon>
        </button>
        <app-launcher />
      </div>
    </div>

    <!-- Menu -->
    <div class="drawer-menu">
      <v-list class="pa-0" dense>
        <template v-for="(item, key) in computeMenu">
          <template v-if="item.children && item.children.length > 0">
            <v-list-group
              :key="key"
              no-action
              :to="item.path"
              color="#9588F0"
              :prepend-icon="getMenuIcon(item)"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    v-text="$vuetify.lang.t('$vuetify.menu.' + item.meta.title)"
                  />
                </v-list-item-content>
              </template>
              <v-list-item
                :class="mini ? 'pl-4' : ''"
                v-for="subItem in item.children"
                :key="subItem.name"
                :to="subItem.path"
                v-show="!subItem.meta.hiddenInMenu"
                color="#9588F0"
              >
                <v-list-item-icon>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="18" color="rgba(255,255,255,0.7)">{{ getMenuIcon(subItem) }}</v-icon>
                    </template>
                    <span>{{ $vuetify.lang.t('$vuetify.menu.' + subItem.meta.title) }}</span>
                  </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content v-if="!mini">
                  <v-list-item-title
                    v-text="$vuetify.lang.t('$vuetify.menu.' + subItem.meta.title)"
                  />
                </v-list-item-content>
              </v-list-item>
            </v-list-group>
          </template>
          <template v-else>
            <v-list-item
              :key="key"
              :to="item.path"
              v-if="canAccessItem(item)"
              v-show="!item.meta.hiddenInMenu"
              color="#9588F0"
            >
              <v-list-item-icon>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" size="20" color="rgba(255,255,255,0.7)">{{ getMenuIcon(item) }}</v-icon>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.menu.' + item.meta.title) }}</span>
                </v-tooltip>
              </v-list-item-icon>
              <v-list-item-content v-if="!mini">
                <v-list-item-title
                  v-text="$vuetify.lang.t('$vuetify.menu.' + item.meta.title)"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </div>

    <!-- Footer: user info + logout icon -->
    <template v-slot:append>
      <div class="drawer-footer" :class="{ 'is-mini': mini }">
        <div class="drawer-user-avatar">
          <v-icon size="18" color="rgba(255,255,255,0.7)">mdi-account</v-icon>
        </div>
        <div class="drawer-user-meta" v-if="!mini && getUser && getUser.attributes">
          <div class="drawer-user-name">
            {{ getUser.attributes.nombre }} {{ getUser.attributes.apaterno }}
          </div>
          <div class="drawer-user-role">{{ getUser.administrator ? 'Administrador' : 'Operador' }}</div>
        </div>
        <button
          v-if="!mini"
          type="button"
          class="drawer-logout-icon"
          data-tip="Cerrar sesion"
          @click="handleLogout"
        >
          <v-icon size="18" color="rgba(255,255,255,0.7)">mdi-logout</v-icon>
        </button>
      </div>
    </template>
  </v-navigation-drawer>
</template>
<script>
import { mapGetters } from 'vuex';
import { sectionMenu } from '@/router/nav-model';
import { canAccess } from '@/router/access';
import AppLauncher from '@/components/nav/AppLauncher.vue';
import logoUrl from '@/assets/logo.svg';

const ICON_MAP = {
  home: 'mdi-view-dashboard',
  users: 'mdi-account-multiple',
  profiles: 'mdi-card-account-details',
  groups: 'mdi-account-group',
  cameras: 'mdi-cctv',
  bus: 'mdi-bus',
  geofences: 'mdi-map-marker-radius',
  'mando-unico': 'mdi-shield-star',
  'mando-unico-geofences': 'mdi-map-marker-radius',
  'mando-unico-region': 'mdi-map-outline',
  'mando-unico-sector': 'mdi-vector-square',
  'mando-unico-cuadrante': 'mdi-grid',
  'lpr-categories': 'mdi-folder-image',
  lpr: 'mdi-camera-front',
  'sensor-categories': 'mdi-folder-alert',
  sensor: 'mdi-alarm-light',
  drivers: 'mdi-account-tie',
  deviceCategories: 'mdi-folder-multiple',
  devices: 'mdi-car-emergency',
  fuelPrice: 'mdi-fuel',
  'video-servers': 'mdi-server-network',
  routes: 'mdi-map-marker-path',
  'incident-categories': 'mdi-shield-alert'
};

export default {
  name: 'AppDrawer',
  components: { AppLauncher },
  props: {
    expanded: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      logoUrl,
      mini: false,
      drawerWidth: 256,
      drawer: true
    };
  },
  computed: {
    ...mapGetters('auth', ['getUser']),
    computeMenu() {
      // Antes leía routes[0].children por índice: reordenar el array de rutas
      // rompía el menú en silencio.
      return sectionMenu('admin', this.getUser);
    }
  },
  methods: {
    getMenuIcon(item) {
      return ICON_MAP[item.name] || item.meta.icon || 'mdi-circle-small';
    },
    handleDrawerCollapse() {
      this.mini = !this.mini;
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    async handleLogout() {
      try {
        await this.$confirm({
          title: 'Cerrar sesión',
          description: '¿Está seguro de que desea cerrar la sesión?',
          acceptText: 'Sí, cerrar sesión',
          cancelText: 'Cancelar',
        });
        this.$store.dispatch('auth/logout');
      } catch (e) {
        // cancelado
      }
    },
    // canAccess es ahora la única implementación de permisos del proyecto; el
    // `validate` local que vivía aquí era una segunda copia que podía divergir
    // de la del guard.
    canAccessItem(item) {
      return canAccess(item, this.getUser);
    },
  },
  watch: {
  '$vuetify.breakpoint.mdAndDown'(isSmall) {
    this.mini = isSmall;
  }
},
mounted() {
  if (this.$vuetify.breakpoint.mdAndDown) {
    this.mini = true;
  }
}
};

</script>

<style lang="scss" scoped>
.app-drawer {
  overflow: hidden !important;
  background: #0D1121 !important;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  min-height: 60px;
  gap: 8px;
}

.drawer-logo {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.drawer-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  &.is-mini {
    flex-direction: column;
    width: 100%;
    align-items: center;
  }
}

.drawer-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.drawer-menu {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.drawer-menu ::v-deep .v-list-group__header .v-list-item__icon .v-icon {
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 20px !important;
}


.drawer-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  &.is-mini {
    justify-content: center;
    padding: 12px 0;
  }
}

.drawer-user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(124, 109, 240, 0.15);
  border: 1px solid rgba(124, 109, 240, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-user-meta {
  min-width: 0;
  flex: 1;
}

.drawer-user-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-user-role {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 1px;
}

.drawer-header-actions .drawer-icon-btn[data-tip]::after {
  top: 110% !important;   
}

.drawer-footer .drawer-logout-icon[data-tip]::after {
  transform: translateX(-70%) !important; 
}

.drawer-logout-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  padding: 0;

  &:hover {
    background: rgba(176, 80, 80, 0.12);
    border-color: rgba(176, 80, 80, 0.3);

    .v-icon {
      color: #D17070 !important;
    }
  }
}
</style>
